import {
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useMediaQuery, useTheme, Tooltip } from "@mui/material";
import TableCellMui from "@mui/material/TableCell";
import {
  TABLE_COLOR_BORDER,
  TABLE_COLOR_BORDER_HEADER,
} from "../../../utils/colors";
import Box from "@mui/material/Box";
import { TableColumnLayoutContext } from "../../../components/Tables/tableColumnLayoutContext";

const DEFAULT_CELL_MIN_WIDTH = 130;

const parseWidthPx = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const ColumnResizeHandle = ({ onMouseDown }) => (
  <Box
    component="button"
    type="button"
    className="column-resize-handle"
    aria-label="Redimensionar columna"
    onMouseDown={onMouseDown}
    onClick={(event) => {
      event.preventDefault();
      event.stopPropagation();
    }}
    sx={{
      position: "absolute",
      top: 0,
      right: 0,
      width: 8,
      height: "100%",
      padding: 0,
      margin: 0,
      border: 0,
      appearance: "none",
      backgroundColor: "transparent",
      cursor: "col-resize",
      zIndex: 3,
      touchAction: "none",
      "&:focus": {
        outline: "none",
      },
      "&:hover, &:active, &:focus-visible": {
        backgroundColor: "rgba(0, 0, 0, 0.12)",
      },
    }}
  />
);

const TableCell = ({
  children,
  align = "left",
  isHeader = false,
  indexRow,
  colSpan = 0,
  onClick = () => {},
  dataTour = "",
  customColor = "",
  isSticky = false,
  stickyOnMobile = false,
  stickyPosition = "right", // "left" or "right"
  stickyOffset = 0, // Offset for multiple sticky columns
  sx = {},
  isLastRow = false,
  showLastRowBorder = false,
  showTooltip = false,
  tooltipComponent = null,
  sxContainerTooltip = {},
  isResizable = false,
  resizable,
  onResize,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [columnIndex, setColumnIndex] = useState(-1);
  const dragRef = useRef(null);
  const cellRef = useRef(null);
  const columnLayout = useContext(TableColumnLayoutContext);
  const canResize = Boolean(
    isHeader && !isMobile && (resizable !== undefined ? resizable : isResizable)
  );
  const managesOwnWidth = typeof onResize === "function";
  const lockedColumnCount = columnLayout.widths?.length ?? 0;
  const parsedMinWidthPx = parseWidthPx(sx.minWidth);
  const parsedWidthPx = parseWidthPx(sx.width);
  const parsedMaxWidthPx = parseWidthPx(sx.maxWidth);
  const tracksCurrentSize =
    parsedMinWidthPx != null &&
    parsedWidthPx != null &&
    parsedMinWidthPx === parsedWidthPx &&
    (parsedMaxWidthPx == null || parsedMaxWidthPx === parsedMinWidthPx);
  const cellMinWidthPx = tracksCurrentSize
    ? Math.min(parsedMinWidthPx, DEFAULT_CELL_MIN_WIDTH)
    : (parsedMinWidthPx ?? DEFAULT_CELL_MIN_WIDTH);

  const isStickyCell = isSticky && (stickyOnMobile || !isMobile);

  useLayoutEffect(() => {
    if (!isHeader && !isStickyCell) return;
    const cell = cellRef.current;
    if (!cell?.parentElement) return;
    const headers = Array.from(cell.parentElement.children);
    const nextIndex = headers.indexOf(cell);
    if (nextIndex !== columnIndex) {
      setColumnIndex(nextIndex);
    }
    if (
      isHeader &&
      lockedColumnCount > 0 &&
      headers.length !== lockedColumnCount
    ) {
      columnLayout.lockColumnWidths?.(null);
    }
    // eslint-disable-next-line
  }, [
    isHeader,
    isStickyCell,
    columnIndex,
    lockedColumnCount,
    columnLayout.lockColumnWidths,
  ]);

  const getBackgroundColor = () => {
    if (customColor) {
      return customColor;
    }
    if (isHeader) {
      return theme?.palette?.table?.header?.background || "#F9FAFB";
    }

    if (indexRow % 2 === 0) {
      return theme?.palette?.table?.cell?.backgroundEven || "#fff";
    }
    return theme?.palette?.table?.cell?.backgroundOdd || "#fff";
  };

  const getTextColor = () => {
    if (isHeader) {
      return "#000";
    }
    return "#000";
  };

  const backgroundColor = getBackgroundColor();
  const color = getTextColor();

  useEffect(() => {
    return () => {
      const drag = dragRef.current;
      if (!drag) return;
      document.removeEventListener("mousemove", drag.onMove);
      document.removeEventListener("mouseup", drag.onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, []);

  const onResizeMouseDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const cell = event.currentTarget.closest("th, td");
    const startX = event.clientX;
    const headers = cell?.parentElement
      ? Array.from(cell.parentElement.children)
      : [];
    const index = cell ? headers.indexOf(cell) : columnIndex;
    const measuredWidths = headers.map((header) =>
      Math.round(header.getBoundingClientRect().width)
    );
    const startWidth =
      measuredWidths[index] ??
      cell?.getBoundingClientRect().width ??
      cellMinWidthPx;

    if (measuredWidths.length > 0) {
      columnLayout.lockColumnWidths?.(measuredWidths);
    }

    const onMove = (moveEvent) => {
      const nextPx = Math.max(
        cellMinWidthPx,
        Math.round(startWidth + moveEvent.clientX - startX)
      );
      if (managesOwnWidth) {
        onResize(nextPx);
      }
      columnLayout.setColumnWidthAt?.(index, nextPx, cellMinWidthPx);
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      dragRef.current = null;
    };

    dragRef.current = { onMove, onUp };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const stickyOffsetValue =
    isStickyCell &&
    stickyPosition === "left" &&
    columnLayout.widths &&
    columnIndex > 0
      ? columnLayout.widths
          .slice(0, columnIndex)
          .reduce((total, width) => total + width, 0)
      : stickyOffset;

  const sxSticky = isStickyCell
    ? {
        position: "sticky",
        [stickyPosition]: stickyOffsetValue,
        zIndex: sx?.zIndex ?? (isHeader ? 1000 : 999),
        backgroundColor: backgroundColor,
      }
    : {};

  const layoutLockedSx = columnLayout.widths
    ? {
        width: "auto",
        minWidth: cellMinWidthPx,
        maxWidth: "none",
      }
    : {};

  const commonSx = {
    padding: "10px 8px !important",
    minWidth: cellMinWidthPx,
    backgroundColor: backgroundColor,
    color,
    fontSize: theme.typography.body2.fontSize,
    fontWeight: isHeader && 600,
    whiteSpace: "nowrap",
    wordBreak: "keep-all",
    overflow: "hidden",
    textOverflow: isHeader ? "clip" : "ellipsis",
    borderBottom:
      isLastRow && !showLastRowBorder
        ? "none"
        : `1px solid ${isHeader ? TABLE_COLOR_BORDER_HEADER : TABLE_COLOR_BORDER}`,
    height: isHeader ? 55 : 70,
    cursor: "pointer",
    ...sx,
    ...layoutLockedSx,
    ...sxSticky,
    ...(canResize
      ? {
          paddingRight: "14px !important",
          "&:hover .column-resize-handle": {
            backgroundColor: "rgba(0, 0, 0, 0.08)",
          },
        }
      : {}),
  };

  const content = (
    <>
      {showTooltip ? (
        <Tooltip
          title={tooltipComponent || children}
          arrow
          PopperProps={{
            sx: {
              zIndex: 9999,
            },
          }}
        >
          <Box
            sx={{
              ...sxContainerTooltip,
              whiteSpace: "nowrap",
              wordBreak: "keep-all",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "inline-block",
            }}
          >
            {children}
          </Box>
        </Tooltip>
      ) : (
        children
      )}
      {canResize && <ColumnResizeHandle onMouseDown={onResizeMouseDown} />}
    </>
  );

  return (
    <TableCellMui
      ref={cellRef}
      data-tour={dataTour}
      align={align}
      colSpan={colSpan}
      onClick={onClick}
      sx={commonSx}
    >
      {content}
    </TableCellMui>
  );
};

export default TableCell;
